import type { KnowledgeSource } from '@/lib/api/workspace-knowledge.js';
import {
  warnsAboutSharing,
  type WorkspaceSharing,
} from '@/features/workspace/workspace-access.js';

import { canManageArtifact } from './artifact-display.js';

/**
 * The knowledge shelf, from the artifacts side. Putting a workspace file on the shelf is curation, not
 * indexing: every file's content is searchable anyway, and the shelf is what makes one of them appear
 * among the workspace's knowledge sources, manageable there — and, in a shared workspace, readable by
 * every member.
 *
 * These helpers are the rules both entries (the Artifacts section and the chat's file panel) share, kept
 * out of the components so one implementation decides them and one test covers it.
 */

/** The shelf row kind. A raw string compare: `type` is an OPEN union on the wire. */
export function isArtifactKnowledgeSource(source: { type: string }): boolean {
  return source.type === 'artifact';
}

/**
 * The shelf row for one file, or undefined when the file is not on the shelf. Matched on
 * `config.artifact_id` — the row's own id is a different id space, used only to address the row in the
 * knowledge routes, so matching on it would find nothing and deleting by it would aim at the wrong thing.
 */
export function findArtifactKnowledgeSource(
  sources: readonly KnowledgeSource[],
  artifactId: string,
): KnowledgeSource | undefined {
  return sources.find(
    (source) =>
      isArtifactKnowledgeSource(source) &&
      source.config.artifact_id === artifactId,
  );
}

/**
 * Whether this caller may put a file on the knowledge shelf or take it off. The service gates both on the
 * SHARE right — the file's author holding editor or better, or a workspace owner — because a promotion
 * opens the file to the workspace, so this mirrors {@link canManageArtifact} rather than the wider edit
 * right. A `viewer` never qualifies, even on their own file.
 *
 * `undefined` is a file the caller cannot resolve in the registry (deleted, or never visible to them):
 * only a workspace owner keeps the action, so nothing renders that the service would answer 403 to.
 */
export function canCurateArtifactKnowledge(
  artifact: { createdBy: string } | undefined,
  viewer: { userId: string | null; role: string | null },
): boolean {
  if (!artifact) return viewer.role === 'owner';
  return canManageArtifact(artifact, viewer);
}

/** The confirmation's words: what is about to happen, and what it costs. */
export interface ArtifactKnowledgeConfirmation {
  title: string;
  description: string;
}

/**
 * What the confirmation says before a file goes on the shelf. In a shared workspace it names the
 * consequence plainly — every member will be able to open the file, and taking it off the shelf later does
 * not close it again. In a PERSONAL workspace there is no consequence to name (there is nobody to share
 * with, and the service leaves the file's visibility alone), so the copy does not mention visibility at
 * all rather than raising a worry that does not apply.
 *
 * A workspace whose kind is not resolved yet gets the shared wording — see {@link warnsAboutSharing}.
 * That says nothing about whether the sharing CONTROLS are offered in that window; they are not.
 */
export function artifactKnowledgeConfirmation(
  files: readonly { name: string }[],
  sharing: WorkspaceSharing,
): ArtifactKnowledgeConfirmation {
  const single = files.length === 1 ? files[0] : undefined;
  const title = single
    ? `Add “${single.name}” to the knowledge base?`
    : `Add ${files.length} files to the knowledge base?`;
  if (!warnsAboutSharing(sharing)) {
    return {
      title,
      description: single
        ? `“${single.name}” will show up in this workspace's knowledge base, where you can reindex it or remove it later.`
        : "The selected files will show up in this workspace's knowledge base, where you can reindex them or remove them later.",
    };
  }
  return {
    title,
    description: single
      ? 'Everyone in this workspace will be able to open this file — adding it to the knowledge base shares it with all members. Removing it from the knowledge base later does not make it private again.'
      : 'Everyone in this workspace will be able to open these files — adding them to the knowledge base shares them with all members. Removing them from the knowledge base later does not make them private again.',
  };
}
